import { useAnalytics } from '@/hooks/useAnalytics';
import { logger } from '@/utils/logger';
import { createContext, useContext, useEffect, useState } from 'react';

/**
 * Analytics consent state
 */
interface AnalyticsConsent {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

/**
 * Analytics context value
 */
interface AnalyticsContextValue {
  consent: AnalyticsConsent | null;
  updateConsent: (consent: Partial<AnalyticsConsent>) => void;
  isEnabled: boolean;
  hasConsent: boolean;
  showConsentBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  isInitialized: boolean;
}

/**
 * Analytics context
 */
const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

/**
 * Hook to access analytics context
 */
export function useAnalyticsContext() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  return context;
}

/**
 * Props for AnalyticsProvider
 */
interface AnalyticsProviderProps {
  children: React.ReactNode;
  /** Whether to show consent banner by default */
  showConsentBanner?: boolean;
  /** Auto-accept analytics for development */
  autoAcceptInDev?: boolean;
}

/**
 * Analytics provider component that manages consent and initialization
 *
 * This component:
 * - Manages analytics consent state across the application
 * - Provides methods to accept/reject analytics
 * - Handles analytics initialization when consent is given
 * - Shows consent banner when needed
 * - Provides context for analytics-related components
 *
 * Place this component high in your component tree, typically in App.tsx
 */
export function AnalyticsProvider({
  children,
  showConsentBanner: showBannerProp = true,
  autoAcceptInDev = false,
}: AnalyticsProviderProps) {
  const analytics = useAnalytics();
  const [showConsentBanner, setShowConsentBanner] = useState(false);

  // Initialize consent banner visibility
  useEffect(() => {
    // Auto-accept in development if configured
    if (autoAcceptInDev && process.env.NODE_ENV === 'development' && !analytics.consent) {
      analytics.updateConsent({
        analytics: true,
        marketing: false,
        functional: true,
      });
      setShowConsentBanner(false);
      logger.debug('Auto-accepted analytics consent in development', 'analytics');
      return;
    }

    // Show consent banner if no consent has been given and it's enabled
    if (showBannerProp && !analytics.consent) {
      setShowConsentBanner(true);
    }
  }, [analytics.consent, showBannerProp, autoAcceptInDev, analytics]);

  /**
   * Accept all analytics and marketing cookies
   */
  const acceptAll = () => {
    analytics.updateConsent({
      analytics: true,
      marketing: true,
      functional: true,
    });
    setShowConsentBanner(false);
    logger.debug('User accepted all analytics consent', 'analytics');
  };

  /**
   * Reject all optional cookies, keep only functional
   */
  const rejectAll = () => {
    analytics.updateConsent({
      analytics: false,
      marketing: false,
      functional: true,
    });
    setShowConsentBanner(false);
    logger.debug('User rejected analytics consent', 'analytics');
  };

  const contextValue: AnalyticsContextValue = {
    consent: analytics.consent,
    updateConsent: analytics.updateConsent,
    isEnabled: analytics.isEnabled,
    hasConsent: analytics.hasConsent,
    showConsentBanner,
    acceptAll,
    rejectAll,
    isInitialized: analytics.isInitialized,
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}

      {/* Consent Banner */}
      {showConsentBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border p-4 shadow-lg">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-sm mb-1">Cookie Preferences</h3>
              <p className="text-sm text-muted-foreground">
                We use cookies to improve your experience and analyze how our galleries are used.
                You can choose which cookies to accept.
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={rejectAll}
                className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted transition-colors"
              >
                Reject All
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </AnalyticsContext.Provider>
  );
}

/**
 * Simple consent banner component that can be used independently
 */
export function ConsentBanner() {
  const { showConsentBanner, acceptAll, rejectAll } = useAnalyticsContext();

  if (!showConsentBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border p-4 shadow-lg">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1">
          <h3 className="font-semibold text-sm mb-1">Cookie Preferences</h3>
          <p className="text-sm text-muted-foreground">
            We use cookies to improve your experience and analyze how our galleries are used. You
            can choose which cookies to accept.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={rejectAll}
            className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted transition-colors"
          >
            Reject All
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsProvider;
